import {
  LegacyPortalShippingService,
  LegacyPortalShippingStatusDispatchStatus,
} from "@spwn-portal/grpc";
import useSWRImmutable from "swr/immutable";
import { useConnectClient } from "utility/connectWeb";

import type { PromiseClient } from "@bufbuild/connect/dist/types/promise-client";
import type { FulfillmentData } from "pages/AccountSettlementHistoryOrderId/types/FulfillmentData";
import type { Store } from "store";

type UseOrderFulfillmentInput = {
  transaction?: Store["purchase"]["activeTransactionMap"][string];
};

type UseOrderFulfillmentOutput = {
  fulfillmentData: FulfillmentData | null;
  isLoading: boolean;
};

const convertDispatchStatusToShippingStatus = (
  status: LegacyPortalShippingStatusDispatchStatus
): string => {
  switch (status) {
    case LegacyPortalShippingStatusDispatchStatus.PACKING:
      return "packing";
    case LegacyPortalShippingStatusDispatchStatus.SHIPPED:
      return "shipped";
    case LegacyPortalShippingStatusDispatchStatus.CANCELLED:
      return "cancelled";
    case LegacyPortalShippingStatusDispatchStatus.UNSPECIFIED:
    default:
      return "suspended";
  }
};

const fetchFulfillmentDataFromBigQuery = async (
  client: PromiseClient<typeof LegacyPortalShippingService>,
  fulfillmentFirestoreId: string
): Promise<FulfillmentData | null> => {
  try {
    const response = await client.getShippingStatusByFulfillmentFirestoreId({
      fulfillmentFirestoreId,
    });
    if (!response.shippingStatus) return null;

    const { status, trackingCode, trackingUrl, deliveryCarrier } =
      response.shippingStatus;

    return {
      carrier: deliveryCarrier,
      trackingCode: trackingCode ?? "",
      shippingStatus: convertDispatchStatusToShippingStatus(status),
      url: trackingUrl ?? "",
    };
  } catch (error) {
    console.error("Failed to fetch fulfillment data from BigQuery:", error);
    return null;
  }
};

export const useOrderFulfillment = (
  input: UseOrderFulfillmentInput
): UseOrderFulfillmentOutput => {
  const { transaction } = input;
  const client = useConnectClient(LegacyPortalShippingService);

  const [fulfillmentRef] = transaction?.fulfillmentRefs ?? [];
  const hasFulfillmentRef = fulfillmentRef !== undefined;
  const fulfillmentFirestoreId = fulfillmentRef?.id;

  const isFetch = hasFulfillmentRef && fulfillmentFirestoreId && client;
  const { data: fulfillmentData = null, isLoading } =
    useSWRImmutable<FulfillmentData | null>(
      isFetch
        ? ["orderFulfillment", transaction?.orderId, fulfillmentFirestoreId]
        : null,
      isFetch
        ? async () =>
            fetchFulfillmentDataFromBigQuery(client, fulfillmentFirestoreId)
        : null
    );

  return {
    fulfillmentData,
    isLoading,
  };
};
